import { ofType } from 'redux-observable';
import { mergeMap, map, filter } from 'rxjs';
import { AppEpic, AppState } from 'src/store';
import { of } from 'rxjs';
import { inputIsNotNullOrUndefined } from 'src/utils/Functions/epicsFunctions';
import {
  mfpModuleReady,
  setActivePage,
  setActivePageComponentPropsConf,
  setActivePerspective,
  setActiveTab,
} from './NavigationShell.slice';
import { extractNavPathFromString, getMfpModule } from './navigationUtils';
import { isEnabledTab } from 'configuration-schemas/confdefn';
import { isNone } from 'fp-ts/lib/Option';
import { updateFilterSensitivity } from 'src/components/FilterPanel/FilterPanel.slice';
import { isNil } from 'lodash';
import { AppThunk } from 'src/store';
import { concatMap, take, tap } from 'rxjs/operators';
import { appReady } from 'src/state/ViewConfig/ViewConfig.slice';
import { Observable } from 'rxjs/internal/Observable';
import { getSettings } from 'src/state/settings/Settings.actions';
import { checkIfHasMfpModule } from 'src/components/higherOrder/MfpScopeSensitive';
import { getAvailableScopeMembers } from 'src/state/scope/Scope.actions';

export const setActivePageComponentPropsEpic: AppEpic = (action$, state$) => {
  return action$.pipe(
    ofType(setActivePage.type),
    map(() => [state$.value.appConfig.tenantConfig, state$.value.perspective.activePage] as const), // const here makes it a 2 elem tuple
    filter(inputIsNotNullOrUndefined),
    mergeMap((stateTuple) => {
      const navPath = extractNavPathFromString(stateTuple[1]);
      if (isNone(navPath)) {
        return of(setActivePageComponentPropsConf(undefined));
      }

      const maybeView = stateTuple[0].tabs
        .filter(isEnabledTab)
        .find((t) => t.pathSlot === navPath.value[1])
        ?.leftNavSections?.find((i) => i.pathSlot === navPath.value[2])
        ?.views.find((v) => v.pathSlot === navPath.value[3]);

      if (maybeView) {
        return of(setActivePageComponentPropsConf(maybeView));
      } else {
        return of(setActivePageComponentPropsConf(undefined));
      }
    })
  );
};


const NON_FILTERABLE_COMPONENTS = [
  'AssortmentCart',
  'ParameterToggles',
  'GeoTrends',
  'MacroMix',
  'TopPerformers',
  'TargetList',
  'Summary'
  // TODO add to list if necessary
];
/**
 * This epic is responsible for setting the page filter sensitivity on any page navigation.
 * It assumes all pages are filter sensitive except a small known set seen above
 *
 */
export const setActivePageFilterSensitivityEpic: AppEpic = (action$, state$) =>
  action$.pipe(
    ofType(setActivePageComponentPropsConf.type),
    map(() => {
      const viewConf = state$.value.perspective.activePageConf;
      // if there's no view (or no component), fall back to filters=on:
      const component = viewConf?.component;
      const filtersOn = !component || !NON_FILTERABLE_COMPONENTS.includes(component);
      return updateFilterSensitivity(filtersOn);
    })
  );

export const checkIfMfpModuleIsReady: AppEpic = (action$, state$) => {
  return action$.pipe(
    ofType(appReady.type, setActivePerspective.type, setActiveTab.type),
    filter(() => checkIfHasMfpModule(state$.value.appConfig.tenantConfig)),
    concatMap((_incoming) => {
      return of(mfpModuleReady());
    })
  );
};

const needsMfpSettings = (state: AppState): boolean => {
  return (
    checkIfHasMfpModule(state.appConfig.tenantConfig) ||
    state.appConfig.tenantConfig.perspective?.scopeConfig?.type === 'MfpScopeSelector'
  );
};

export const setupSettings: AppEpic = (action$, state$, deps): Observable<AppThunk> => {
  const settingsService = deps.settings;
  return action$.pipe(
    ofType(appReady.type, mfpModuleReady.type),
    filter(() => needsMfpSettings(state$.value)),
    concatMap((_incoming) => {
      return of(getSettings(settingsService));
    })
  );
};

export const getMfpScopeMembers: AppEpic = (action$, state$, deps): Observable<AppThunk> => {
  const settingsService = deps.settings;
  return action$.pipe(
    ofType(appReady.type, mfpModuleReady.type),
    filter(() => needsMfpSettings(state$.value)),
    map(() => getMfpModule(state$.value)),
    filter(inputIsNotNullOrUndefined),
    concatMap((mfpModule) => of(getAvailableScopeMembers(mfpModule)))
  );
};

export const setLocalization: AppEpic = (action$, _state$, deps) => {
  const localizationService = deps.localization;
  return action$.pipe(
    ofType(getSettings.fulfilled.type),
    take(1),
    tap((action) => {
      // WARNING: here by side effects!
      localizationService.setCulture(action.payload.localization.baseLocale);
    })
  );
};
